import type { ReactNode } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import SiteNav, { BookingButton, WriteMeLink } from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageSeo from "@/components/site/PageSeo";

export const COMPARISON_SLUG = "cmo-externalise-vs-agence-vs-recrutement";
export const COMPARISON_PATH = `/blog/${COMPARISON_SLUG}`;
export const COMPARISON_TITLE = "CMO externalisé, agence marketing ou CDI : comparatif 2026 | CMO Partner";
export const COMPARISON_DESCRIPTION =
  "Fractional CMO, agence ou recrutement en CDI : coûts, délais, rôle et engagement comparés. Le guide pour choisir la bonne option selon la taille et le stade de votre PME.";
export const COMPARISON_H1 = "CMO externalisé, agence marketing ou CDI : le comparatif complet 2026";
const URL = `https://www.cmopartner.fr${COMPARISON_PATH}`;

export const COMPARISON_FAQ = [
  { q: "Peut-on combiner un Fractional CMO et une agence ?", a: "Oui, c'est même la combinaison la plus fréquente dans les PME en croissance. Le CMO externalisé définit la stratégie et le brief, l'agence exécute les campagnes, le CMO pilote les résultats et ajuste le plan." },
  { q: "Un CMO externalisé peut-il manager une équipe interne ?", a: "Oui, c'est l'un des principaux intérêts du format : donner un cap à une équipe qui exécute sans direction stratégique. Le management s'adapte à la présence partielle, avec des rituels à distance et un reporting structuré." },
  { q: "Quelle est la différence de coût réel entre un Fractional CMO et un CDI ?", a: "Un CMO senior en CDI coûte 80 à 150 k€ par an en coût total employeur, auxquels s'ajoutent 3 à 6 mois de recrutement et le risque d'erreur de casting. Un Fractional CMO est facturé selon le nombre de jours réellement nécessaires (de 2 à 12 par mois) : vous payez l'expertise senior au temps utile, sans charges sociales ni engagement long. Le détail des formats est dans notre guide des tarifs.", link: { to: "/blog/fractional-cmo-definition-missions-tarifs", label: "Lire le guide des tarifs" } },
  { q: "Combien de temps faut-il pour recruter un bon CMO ?", a: "En moyenne 3 à 6 mois entre le lancement de la recherche et la prise de poste, parfois davantage pour un profil senior dans un secteur spécifique. Un directeur marketing à temps partagé peut démarrer sous 2 semaines et assurer la continuité pendant ce temps." },
];

const PERSON = { "@type": "Person", name: "Tiphany Rigaudias", jobTitle: ["Fractional CMO", "Directrice marketing externalisée", "Directrice digitale externalisée"], knowsAbout: ["Marketing B2B", "CRM", "Marketing automation", "IA appliquée au marketing", "Formation des équipes marketing"], url: "https://www.cmopartner.fr/a-propos", sameAs: ["https://linkedin.com/in/tiphanyrigaudias"] };

export const COMPARISON_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: COMPARISON_H1,
    description: COMPARISON_DESCRIPTION,
    datePublished: "2026-09-28",
    dateModified: "2026-09-28",
    inLanguage: "fr-FR",
    author: PERSON,
    publisher: { "@type": "Organization", name: "CMO Partner", url: "https://www.cmopartner.fr/", founder: PERSON },
    mainEntityOfPage: { "@type": "WebPage", "@id": URL },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: COMPARISON_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://www.cmopartner.fr/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.cmopartner.fr/blog" },
      { "@type": "ListItem", position: 3, name: "CMO externalisé, agence marketing ou CDI", item: URL },
    ],
  },
];

const H2 = ({ children }: { children: string }) => (
  <h2 className="text-2xl font-bold leading-tight md:text-3xl">
    {children}
    <span className="text-primary">.</span>
  </h2>
);
const H3 = ({ children }: { children: string }) => <h3 className="mt-10 text-xl font-bold">{children}</h3>;
const P = ({ children }: { children: ReactNode }) => <p className="mt-5 text-lg leading-relaxed text-muted-foreground">{children}</p>;
const A = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="text-foreground underline underline-offset-4 hover:text-primary">{children}</Link>
);
const Bullets = ({ items }: { items: ReactNode[] }) => (
  <ul className="mt-5 space-y-3">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3 text-lg leading-relaxed text-muted-foreground">
        <span className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const tableHead = ["Critère", "CMO externalisé (Fractional)", "Agence marketing", "Recrutement en CDI"];
const tableRows = [
  ["Profil", "Directeur marketing senior à temps partiel", "Équipe de spécialistes externes", "Collaborateur interne à temps plein"],
  ["Rôle", "Stratégie et pilotage de l'exécution", "Exécution de campagnes et de contenus", "Stratégie et exécution selon le niveau"],
  ["Coût", "Sur devis, selon le format (2 à 12 jours par mois)", "Environ 2 000 à 10 000 € HT par mois", "80 à 150 k€ par an en coût total employeur"],
  ["Délai de démarrage", "1 à 2 semaines", "2 à 4 semaines", "3 à 6 mois (recrutement et intégration)"],
  ["Durée type", "6 à 12 mois", "Long terme", "Long terme"],
  ["Management de l'équipe interne", "Oui", "Non", "Oui"],
  ["Engagement", "Faible (sortie possible à 3 mois)", "Variable (souvent 6 à 12 mois)", "Fort (préavis, rupture)"],
];

const options: { title: string; what: ReactNode; who: string[]; pros: string[]; cons: string[] }[] = [
  {
    title: "Le CMO externalisé (Fractional CMO)",
    what: <>Un directeur marketing senior qui travaille pour vous de 2 à 12 jours par mois selon le format retenu. Il prend la responsabilité de la fonction marketing : stratégie, coordination des prestataires, management de l'équipe interne si elle existe, reporting au dirigeant. Pour aller plus loin : notre page <A to="/fractional-cmo">Fractional CMO</A> et le détail de l'offre de <A to="/directeur-marketing-externalise">directeur marketing externalisé</A>.</>,
    who: ["PME et ETI de 10 à 300 personnes en phase de structuration ou de croissance", "Entreprises qui ont une équipe marketing mais pas de direction stratégique", "Situations de transition (départ d'un CMO, préparation d'une levée de fonds)", "Budgets insuffisants pour un CDI senior, mais besoin d'une expertise senior"],
    pros: ["Une expertise senior immédiate, sans délai de recrutement", "Une flexibilité sur la durée et l'intensité (on peut démarrer en intensif puis alléger)", "Une prestation : pas de charges sociales, pas de période d'essai, pas de rupture de contrat", "Un regard extérieur, sans angle mort interne", "Une expérience multisectorielle transposable à votre contexte"],
    cons: ["Une présence physique plus limitée qu'un salarié à temps plein", "Une mission bornée dans le temps", "Un transfert de compétences à organiser pour la suite"],
  },
  {
    title: "L'agence marketing",
    what: "Une agence réunit des spécialistes (SEO, publicité, contenu, design, réseaux sociaux) qui travaillent sur vos campagnes à partir d'un brief. Elle exécute, mais ne définit pas votre stratégie d'ensemble.",
    who: ["Entreprises qui ont déjà une direction marketing interne et cherchent de la production", "Projets bornés dans le temps (lancement de produit, campagne saisonnière)", "PME qui veulent externaliser une spécialité précise (SEA, influence, vidéo)"],
    pros: ["Accès à des compétences spécialisées difficiles à recruter", "Capacité à augmenter rapidement les volumes de production", "Pas de charge managériale"],
    cons: ["Ne remplace pas une direction stratégique", "Aligne ses actions sur le brief reçu, pas nécessairement sur vos objectifs business", "Rotation des équipes et perte de contexte fréquentes", "Coût élevé au regard des résultats quand le brief est flou"],
  },
  {
    title: "Le recrutement d'un CMO en CDI",
    what: "L'embauche d'un directeur ou d'un responsable marketing interne, à temps plein, avec les droits et obligations d'un contrat de travail.",
    who: ["Entreprises de plus de 150 personnes, avec un budget marketing significatif (plusieurs centaines de milliers d'euros par an)", "Structures où le marketing est central et permanent dans le modèle économique", "Entreprises prêtes à investir sur 3 à 5 ans dans un profil"],
    pros: ["Un engagement et une implication maximaux", "Le développement d'une culture marketing interne sur le long terme", "Une disponibilité totale"],
    cons: ["Un délai de recrutement de 3 à 6 mois en moyenne pour un profil senior", "Un coût total élevé : salaire, charges, avantages, intégration", "Un risque d'erreur de casting coûteux sur un poste de direction"],
  },
];

const stages = [
  ["Moins de 50 personnes et pas de direction marketing", "commencez par un Fractional CMO. Il structure la fonction, choisit les outils, pose les processus et vous aide à recruter le bon profil le moment venu."],
  ["50 à 150 personnes, une équipe marketing sans stratégie claire", "un Fractional CMO complété par une agence pour les spécialités techniques (SEA, SEO). Le CMO externalisé apporte la direction, l'agence l'exécution spécialisée."],
  ["Plus de 150 personnes et un marketing stratégique", "un CDI. Un CMO à temps partagé peut intervenir en amont pour définir le profil et sécuriser la transition."],
  ["Un projet précis et borné dans le temps", "une agence, avec un brief stratégique clair fourni par vous ou par un directeur marketing externalisé."],
];

const readNext = [
  { label: "Fractional CMO : tarifs, formats et missions", to: "/blog/fractional-cmo-definition-missions-tarifs" },
  { label: "Directeur marketing externalisé", to: "/directeur-marketing-externalise" },
  { label: "Stratégie marketing pour PME à Bordeaux", to: "/blog/strategie-marketing-pme-bordeaux" },
];

const CmoComparisonArticle = () => (
  <div className="min-h-screen bg-background text-foreground">
    <PageSeo path={COMPARISON_PATH} title={COMPARISON_TITLE} description={COMPARISON_DESCRIPTION} />
    <SiteNav />
    <main>
      <article>
        <header className="border-b border-border px-5 pb-14 pt-20 md:pt-28">
          <div className="mx-auto max-w-3xl">
            <nav className="mb-8 text-sm text-muted-foreground" aria-label="Fil d'Ariane">
              <Link to="/" className="hover:text-primary">Accueil</Link>
              <span className="px-2">/</span>
              <Link to="/blog" className="hover:text-primary">Blog</Link>
              <span className="px-2">/</span>
              <span className="text-foreground">CMO externalisé, agence marketing ou CDI</span>
            </nav>
            <h1 className="text-4xl font-bold leading-[1.1] md:text-5xl">{COMPARISON_H1}</h1>
            <p className="mt-8 text-sm text-muted-foreground">
              Par <Link to="/a-propos" className="font-semibold text-foreground hover:text-primary">CMO Partner — Fractional CMO</Link> · Mis à jour : <time dateTime="2026-09-28">septembre 2026</time>
            </p>
            <P>Quand une PME décide de structurer sa fonction marketing, trois options s'offrent à elle. Chacune a ses avantages, ses limites, et correspond à un stade de maturité différent. Ce comparatif vous aide à choisir selon votre situation réelle.</P>
          </div>
        </header>

        <div className="px-5 py-16 md:py-20">
          <div className="mx-auto max-w-3xl space-y-16">
            <section>
              <H2>Les 3 options en bref</H2>
              <div className="mt-8 overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[760px] text-left text-sm md:text-base">
                  <thead className="bg-secondary">
                    <tr>{tableHead.map((h) => <th key={h} scope="col" className="px-5 py-4 font-semibold">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {tableRows.map((r) => (
                      <tr key={r[0]} className="border-t border-border">
                        {r.map((c, i) => <td key={i} className={`px-5 py-4 align-top ${i === 0 ? "font-semibold" : "text-muted-foreground"}`}>{c}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {options.map((o, idx) => (
              <section key={o.title}>
                <H2>{o.title}</H2>
                <H3>Ce que c'est</H3>
                <P>{o.what}</P>
                <H3>Pour qui</H3>
                <Bullets items={o.who} />
                <H3>Avantages</H3>
                <Bullets items={o.pros} />
                <H3>Limites</H3>
                <Bullets items={o.cons} />
                {idx === 0 && (
                  <div className="mt-10 rounded-lg bg-secondary p-7">
                    <p className="text-lg font-semibold">Vous hésitez entre ces options ? Faisons le point en 30 minutes.</p>
                    <div className="mt-5"><BookingButton label="Réserver un appel" location="blog_comparison_mid" /></div>
                  </div>
                )}
              </section>
            ))}

            <section>
              <H2>Comment choisir selon votre stade ?</H2>
              <ol className="mt-5 space-y-4">
                {stages.map(([t, d], i) => (
                  <li key={t} className="flex gap-4 text-lg leading-relaxed text-muted-foreground">
                    <span className="font-bold text-primary">{String(i + 1).padStart(2, "0")}</span>
                    <span><strong className="text-foreground">{t}</strong> : {d}</span>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </div>
      </article>

      <section className="border-t border-border bg-secondary px-5 py-20">
        <div className="mx-auto max-w-3xl">
          <H2>FAQ : CMO externalisé, agence ou CDI</H2>
          <div className="mt-10">
            {COMPARISON_FAQ.map((f) => (
              <details key={f.q} className="group border-t border-border py-6">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-bold">
                  {f.q}
                  <span className="text-primary transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 leading-relaxed text-muted-foreground">
                  {f.a}{f.link && <> <A to={f.link.to}>{f.link.label}</A>.</>}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border px-5 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-xl font-bold">À lire aussi</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {readNext.map((r) => (
              <Link key={r.to} to={r.to} className="rounded-lg border border-border p-5 transition-colors hover:border-primary">
                <span className="font-semibold">{r.label}</span>
                <ArrowRight className="mt-4 h-4 w-4 text-primary" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="booking" className="scroll-mt-16 bg-foreground px-5 py-24 text-center text-background md:py-28">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold md:text-5xl">Quelle option pour votre entreprise<span className="text-primary"> ?</span></h2>
          <p className="mt-6 text-lg text-background/60">30 minutes pour faire le point sur votre situation et choisir la bonne organisation marketing.</p>
          <div className="mt-10"><BookingButton label="Réserver un appel de 30 minutes" location="blog_comparison_final" large /><WriteMeLink /></div>
          <p className="mt-6 text-sm text-background/50">Basée à Bordeaux · Disponible partout en France</p>
        </div>
      </section>
    </main>
    <SiteFooter />
  </div>
);

export default CmoComparisonArticle;
